const HEX_RE = /^#[0-9a-fA-F]{6}$/;

/** Validate a hex color string (e.g. "#c5a46e") */
export function isValidHex(hex: string): boolean {
  return HEX_RE.test(hex);
}

/** Convert hex color to "H S% L%" string matching CSS variable format */
export function hexToHsl(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;

  if (max === min) return `0 0% ${Math.round(l * 100)}%`;

  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

  let h: number;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/** Adjust the lightness of an HSL string by a delta (e.g. +23 or -10) */
export function adjustLightness(hsl: string, delta: number): string {
  const match = hsl.match(/^(\d+)\s+(\d+)%\s+(\d+)%$/);
  if (!match) return hsl;
  const newL = Math.min(100, Math.max(0, parseInt(match[3]) + delta));
  return `${match[1]} ${match[2]}% ${newL}%`;
}

/**
 * Returns an appropriate foreground color (white or black in HSL format)
 * based on the relative luminance of the given hex color.
 */
export function getContrastForeground(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;

  // sRGB relative luminance
  const lum =
    0.2126 * (r <= 0.03928 ? r / 12.92 : ((r + 0.055) / 1.055) ** 2.4) +
    0.7152 * (g <= 0.03928 ? g / 12.92 : ((g + 0.055) / 1.055) ** 2.4) +
    0.0722 * (b <= 0.03928 ? b / 12.92 : ((b + 0.055) / 1.055) ** 2.4);

  return lum > 0.179 ? '0 0% 0%' : '0 0% 100%';
}

/** Lighten a hex color by a percentage (0–1). */
export function lightenHex(hex: string, amount = 0.2): string {
  const c = hex.replace('#', '');
  const r = Math.min(255, Math.round(parseInt(c.substring(0, 2), 16) * (1 + amount)));
  const g = Math.min(255, Math.round(parseInt(c.substring(2, 4), 16) * (1 + amount)));
  const b = Math.min(255, Math.round(parseInt(c.substring(4, 6), 16) * (1 + amount)));
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

/** Check if a hex color is dark (luminance < 0.5). */
export function isColorDark(hex: string): boolean {
  const c = hex.replace('#', '');
  const r = parseInt(c.substring(0, 2), 16) / 255;
  const g = parseInt(c.substring(2, 4), 16) / 255;
  const b = parseInt(c.substring(4, 6), 16) / 255;
  const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return lum < 0.5;
}

